/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useState, useMemo } from "react";
import useScrollMemory from "../hooks/useScrollMemory";
import BioIndexExploreLevelLayout, { BioIndexTaxonCard } from "../components/BioIndexExploreLevelLayout";
import { useParams, useLocation } from "react-router-dom";
import { iconUrl, defaultImg } from "../utils/icons";
import { generaByFamily } from "../utils/taxonomy";
import { collection, getDocs } from "firebase/firestore";

import { db, auth } from "../firebase";


function getGenusImage(genusName) {
  if (!genusName) return defaultImg;

  const filename = `${genusName}.png`;
  return iconUrl(filename);
}

function GenusPage() {
  useScrollMemory();

  const location = useLocation();

  const { kingdomName, phylumName, className, orderName, familyName, uid } =
    useParams();

  const currentUID = auth.currentUser?.uid;
  const dataUID = uid || currentUID;

  const adminModeActive =
    !!uid && new URLSearchParams(location.search).get("adminMode") === "true";

  const adminModeSuffix = adminModeActive ? "?adminMode=true" : "";

  const genera = useMemo(
    () => generaByFamily[familyName.toLowerCase()] || [],
    [familyName],
  );

  // 🔹 Species count state
  const [speciesCounts, setSpeciesCounts] = useState({});
  const [countsLoading, setCountsLoading] = useState(true);

  const [browseMode, setBrowseMode] = useState("taxonomy");
  useEffect(() => {
    let cancelled = false;

    async function loadCounts() {
      try {
        setCountsLoading(true);

        if (!genera || genera.length === 0) {
          if (!cancelled) {
            setSpeciesCounts({});
            setCountsLoading(false);
          }
          return;
        }

        const snapshot = await getDocs(
          collection(db, "users", dataUID, "species"),
        );

        const genusSet = new Set(genera);
        const counts = {};

        snapshot.forEach((speciesDoc) => {
          const genus = speciesDoc.data()?.genus;

          if (!genus || !genusSet.has(genus)) return;

          counts[genus] = (counts[genus] || 0) + 1;
        });

        if (!cancelled) {
          setSpeciesCounts(counts);
          setCountsLoading(false);
        }
      } catch (err) {
        console.error("Failed to load species counts:", err);
        if (!cancelled) {
          setSpeciesCounts({});
          setCountsLoading(false);
        }
      }
    }

    loadCounts();

    return () => {
      cancelled = true;
    };
  }, [genera, dataUID]);

  const visibleItems = genera.filter(
    (item) => Number(speciesCounts[item] || 0) > 0,
  );
  const totalSpecies = visibleItems.reduce(
    (sum, item) => sum + Number(speciesCounts[item] || 0),
    0,
  );
  const routePrefix = uid ? `/explore/${uid}` : "";

  return (
    <BioIndexExploreLevelLayout
      uid={uid}
      dataUID={dataUID}
      currentLevel="genus"
      params={{ kingdomName, phylumName, className, orderName, familyName }}
      browseMode={browseMode}
      setBrowseMode={setBrowseMode}
      isOwnCollection={!uid || uid === currentUID}
      itemName="genera"
      itemCount={visibleItems.length}
      secondaryCount={totalSpecies}
      adminModeActive={adminModeActive}
    >
      {countsLoading ? (
        <div className="bioindex-explore-empty" role="status">
          <span className="bioindex-explore-spinner" aria-hidden="true" />
          <h2>Loading genera</h2>
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="bioindex-explore-empty">
          <h2>No genera to explore yet</h2>
          <p>There are no documented species in this group yet.</p>
        </div>
      ) : (
        <div className="bioindex-explore-kingdom-grid">
          {visibleItems.map((item) => {
            const key = item;
            const count = Number(speciesCounts[key] || 0);
            const path = `/kingdom/${kingdomName}/phylum/${phylumName}/class/${className}/order/${orderName}/family/${familyName}/genus/${key}`;
            const to = uid
              ? `${routePrefix}${path}${adminModeSuffix}`
              : path;
            return (
              <BioIndexTaxonCard
                key={item}
                name={item}
                count={count}
                image={getGenusImage(item)}
                to={to}
                level="genus"
                italic={true}
              />
            );
          })}
        </div>
      )}
    </BioIndexExploreLevelLayout>
  );

}
export default GenusPage;
